import { useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { usePropertyDetail } from "../../../hooks/pages/property/usePropertyDetail";
import { useExpandableDescription } from "../../../hooks/pages/property/useExpandableDescription";
import PropertyGallery from "../../../components/pages/PropertyDetails/PropertyGallery";
import PropertieSpecs from "../../../components/pages/PropertyDetails/PropertySpecs";
import PropertySummaryBox from "../../../components/pages/PropertyDetails/PropertySummaryBox";
import PropertyInfoCard from "../../../components/pages/PropertyDetails/PropertyInfoCard";
import {
  FaSwimmer, FaDumbbell, FaChild, FaBuilding, FaShieldAlt,
  FaParking, FaTree, FaFire, FaShoppingCart, FaSchool,
} from "react-icons/fa";
import { API_URL } from "../../../config/api";

const parseImages = (images) => {
  if (!images) return [];
  if (Array.isArray(images)) return images;
  try {
    const parsed = JSON.parse(images);
    if (Array.isArray(parsed)) return parsed;
  } catch {}
  return [images];
};

const amenitiesIcons = {
  "Hồ bơi": <FaSwimmer className="text-blue-500" />,
  Gym: <FaDumbbell className="text-gray-700" />,
  "Khu vui chơi": <FaChild className="text-yellow-500" />,
  "Bảo vệ 24/7": <FaShieldAlt className="text-green-500" />,
  "Bãi đỗ xe": <FaParking className="text-gray-600" />,
  "Thang máy": <FaBuilding className="text-gray-800" />,
  "Sân vườn": <FaTree className="text-green-700" />,
  BBQ: <FaFire className="text-red-500" />,
  "Trung tâm thương mại": <FaShoppingCart className="text-purple-600" />,
  "Công viên": <FaTree className="text-green-500" />,
  "Trường học": <FaSchool className="text-blue-700" />,
};

export default function CarDetail() {
  const { id } = useParams();

  const { property, similarProperties, loading } = usePropertyDetail(id);

  const {
    showFullDescription,
    setShowFullDescription,
    displayedDescription,
    isLong,
  } = useExpandableDescription(property?.description);

  const scrollContainerRef = useRef(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  // Reset khi đổi id
  useEffect(() => {
    setShowFullDescription(false);
    setShowScrollIndicator(false);
  }, [id, setShowFullDescription]);

  // Kiểm tra scroll mô tả
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const checkScroll = () => {
      const isScrollable = container.scrollHeight > container.clientHeight;
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < 50;
      setShowScrollIndicator(isScrollable && !isNearBottom);
    };

    const timer = setTimeout(checkScroll, 100);
    container.addEventListener("scroll", checkScroll);
    return () => {
      container.removeEventListener("scroll", checkScroll);
      clearTimeout(timer);
    };
  }, [property]);

  // Scroll lên đầu khi đổi id
  useEffect(() => {
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 50);
    return () => clearTimeout(timer);
  }, [id]);

  if (loading) return <div className="text-center py-10">Đang tải dữ liệu...</div>;
  if (!property) return null;

  // Xử lý ảnh
  const images = parseImages(property.images);
  const firstImage = images[0] ? `${API_URL}${images[0]}` : "";

  // Xử lý amenities
  let amenities = [];
  try {
    amenities = Array.isArray(property.amenities)
      ? property.amenities
      : JSON.parse(property.amenities || "[]");
  } catch {
    amenities = [];
  }

  return (
    <>
      <Helmet>
        <title>{property.projectName} | {property.developer} - Vinhomes</title>
        <meta name="description" content={property.description?.slice(0, 150) || "Thông tin chi tiết bất động sản."} />
        <meta property="og:title" content={`${property.projectName} | ${property.developer}`} />
        <meta property="og:description" content={property.description?.slice(0, 150) || "Thông tin chi tiết bất động sản."} />
        <meta property="og:image" content={firstImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ApartmentComplex",
            name: property.projectName,
            description: property.description,
            address: {
              "@type": "PostalAddress",
              streetAddress: property.address || "",
              addressLocality: property.district || "",
              addressRegion: property.province || "",
              addressCountry: "VN",
            },
            image: images.map((img) => `${API_URL}${img}`),
            offers: {
              "@type": "Offer",
              priceCurrency: "VND",
              price: property.price || 0,
              availability: "https://schema.org/InStock",
            },
            seller: {
              "@type": "Organization",
              name: property.developer || "Chủ đầu tư",
            },
            url: window.location.href,
          })}
        </script>
      </Helmet>

      <div className="max-w-7xl mx-auto p-4">
        <div className="text-sm text-blue-500 mb-2">
          Trang chủ / {property.projectName}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LEFT */}
          <div className="lg:col-span-2">
            <PropertyGallery key={property.id} images={images} name={property.name} />
            <PropertieSpecs property={property} />

            {/* DESCRIPTION */}
            <div className="mt-8">
              <h2 className="text-lg font-semibold text-blue-900 mb-2">Mô tả</h2>
              <div
                ref={scrollContainerRef}
                className="border p-4 rounded text-sm text-blue-900 whitespace-pre-line"
              >
                {displayedDescription}
                {isLong && (
                  <div className="mt-4 text-right">
                    <button
                      onClick={() => setShowFullDescription(!showFullDescription)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium underline transition"
                    >
                      {showFullDescription ? "Thu gọn ▲" : "Xem thêm ▼"}
                    </button>
                  </div>
                )}
              </div>
              {showScrollIndicator && (
                <div className="text-center text-blue-500 animate-bounce mt-2">
                  ▼ Cuộn để xem thêm
                </div>
              )}

              {/* AMENITIES */}
              {amenities.length > 0 && (
                <div className="mt-8">
                  <h2 className="text-lg font-semibold text-blue-900 mb-2">Tiện ích nổi bật</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {amenities.map((amenity, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg p-2 text-sm text-blue-800"
                      >
                        {amenitiesIcons[amenity] || <span className="w-2 h-2 bg-blue-600 rounded-full" />}
                        {amenity}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT */}
          <div className="lg:sticky lg:top-6 h-fit">
            <PropertySummaryBox property={property} />
          </div>
        </div>

        {/* SIMILAR PRODUCTS */}
        {similarProperties.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-semibold text-blue-900 mb-4">Sản phẩm tương tự</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {similarProperties.map((item) => (
                <PropertyInfoCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}